import { Sprite, type Texture } from 'pixi.js';
import type { EnemyDef, StatusApply } from '../core/types';
import type { Look } from './looks';
import type { EnemyArt } from './enemyart';
import type { BossBrain } from './bosses';

/** 풀에 들어가는 개체들. 생성자에서만 할당하고 그 뒤로는 값만 갈아끼운다. */

export class Enemy {
  uid = 0; // 관통 무기가 같은 적을 두 번 때리지 않게 하는 식별자
  x = 0;
  y = 0;
  vx = 0; // 넉백 속도(감쇠한다). 이동 속도와 별개.
  vy = 0;
  hp = 0;
  maxHp = 0;
  damage = 0;
  speed = 0;
  radius = 0;
  xp = 0;
  boss = false;
  dead = false; // 이번 프레임에 죽었다는 표시. 실제 제거는 프레임 끝의 sweep에서 한다.
  baseTint = 0xffffff;
  flash = 0; // 피격 점멸 타이머
  slowT = 0;
  slowAmt = 0;
  def!: EnemyDef;
  /** 무기 슬롯별 재타격 쿨다운. 지속 타격 무기(궤도/오라)가 매 프레임 때리는 걸 막는다. 개발 패널이 고유 10종을 한꺼번에 넣어도 되게 16칸 */
  readonly wepCd = new Float32Array(16);

  // ── 상태효과 (살바트리스 키워드) ──
  /** [화염] 중첩. 주기마다 중첩만큼 피해를 입고 사라진다(burnLock 중에는 남는다). */
  burn = 0;
  /** 지금 붙은 [화염] 한 중첩의 피해 — 불을 건 스킬들의 값을 중첩 수만큼 가중 평균한다 */
  burnDmg = 0;
  burnTimer = 0;
  /** [서리] 중첩. 8 이상이면 [빙결] (config STATUS.FROST_FREEZE_AT). */
  frost = 0;
  /** [빙결] 남은 시간(초). 움직일 수 없고 받는 피해 1.2배. */
  frozen = 0;
  /** [빙결]이 풀린 뒤 서리가 다시 쌓이지 않는 시간 */
  frostImmune = 0;
  /** [기절] 남은 시간(초). */
  stun = 0;
  /** 광풍에 휘말려 공중에 뜬 남은 시간(초). 뜬 동안은 못 움직이고 빙글 돌며 날아간다 */
  airT = 0;
  /** 이번에 뜬 전체 시간·가장 높이 뜬 높이(px)·그동안 도는 바퀴 수 */
  airDur = 0;
  airH = 0;
  airTurns = 0;
  /** 다시 휘말릴 수 있을 때까지(초) — 회오리 하나가 한 적을 줄곧 띄워 두지 못하게 */
  airCd = 0;
  /** [감전] 보유 여부. 한 번에 큰 피해를 받으면 주변으로 터진다. */
  shock = false;
  /** 0보다 크면 [화염]이 꺼지지 않는다(초). */
  burnLock = 0;

  // ── 행동 상태 ──
  /** 단계 타이머 */
  aiT = 0;
  /** 0=평소 1=준비 2=실행 */
  aiPhase = 0;
  /** 돌진 방향 (고정해두고 그쪽으로만 간다) */
  dashX = 0;
  dashY = 0;
  /** 쪼개져 나온 놈은 또 쪼개지지 않는다 */
  noSplit = false;
  /** 그리는 크기 배율 (쪼개진 새끼는 작다) */
  drawScale = 1;
  /** 나타나는 중 남은 시간(초). 0보다 크면 그림이 반투명이다(판정은 그대로). */
  fadeIn = 0;
  /** 엘리트 — 크고 단단하고 금빛. 죽으면 보물 상자를 떨군다 */
  elite = false;
  /** 1.23 잡몹 단계 (1 = 처음). 나온 시각에 정해진다 — config mobTier */
  tier = 1;
  /** 이어서 한 횟수 (덮치기·할퀴기) */
  chain = 0;
  /** 상태효과 반응(증기 폭발·쇄빙·작렬)이 다시 일어나지 않는 시간 */
  reactT = 0;

  // ── 1.22 도트 그림 ──
  art: EnemyArt | null = null;
  /** 지금 그리는 동작 · 그 안의 시간 */
  anim = 'move';
  animT = 0;
  /** 걷는 줄 (미물은 세 줄 가운데 하나) */
  moveAnim = 'move';
  /** 보는 쪽 1 오른쪽 · -1 왼쪽 */
  face = 1;
  /** 발을 몸 가운데(판정 원)에서 이만큼 아래로 그린다 */
  foot = 0;
  artScale = 1;
  /** 쏜 횟수 (모아 쏘기) · 쏘기 전 손을 모으는 남은 시간 */
  shots = 0;
  castT = 0;
  /** 보스의 머리 (기술) */
  brain: BossBrain | null = null;
  readonly sprite = new Sprite();
  /** 발밑 그림자 */
  readonly shadow = new Sprite();
  /** 엘리트 금빛 덧칠 (흰 실루엣을 더하기로) */
  readonly gild = new Sprite();

  constructor() {
    this.sprite.anchor.set(0.5);
    this.sprite.visible = false;
    this.shadow.anchor.set(0.5);
    this.shadow.visible = false;
    this.gild.visible = false;
    this.gild.blendMode = 'add';
  }
}

export class Projectile {
  x = 0;
  y = 0;
  vx = 0;
  vy = 0;
  damage = 0;
  life = 0;
  pierce = 0;
  radius = 0;
  knockback = 0;
  spin = 0;
  hits: number[] = [0, 0, 0, 0, 0, 0, 0, 0];
  hitCount = 0;
  /** 명중 시 거는 상태효과 */
  status: StatusApply | null = null;
  /** 사라질 때 터지는 반경. 0이면 안 터진다. */
  explodeRadius = 0;
  explodeDamage = 0;
  /** 좌우 반폭. 0이 아니면 진행 방향에 가로로 누운 띠로 맞는다(파도). */
  halfWidth = 0;
  /** 하늘에서 떨어지는 탄 — 날아가는 동안은 안 맞고, 떨어진 자리가 터진다 */
  fall = false;
  /** 터진 자리에 불이 남는다 */
  pool = false;
  /** 맞힐 때마다 입힌 피해의 이 비율만큼 플레이어 체력을 되찾는다 */
  lifesteal = 0;
  /** 폭발 연출 색 (무기 속성 램프) */
  attr = 'white';
  /** 번갈아 보여 줄 그림 (불길·전기가 일렁이게). 한 장이면 null */
  frames: Texture[] | null = null;
  animT = 0;
  /** 쏜 무기의 겉모습 — 맞는 소리, 폭발 모양 */
  look: Look | null = null;
  /** 폭발 연출 색 램프 */
  ramp: readonly string[] | null = null;
  readonly sprite = new Sprite();

  constructor() {
    this.sprite.anchor.set(0.5);
    this.sprite.visible = false;
  }

  alreadyHit(uid: number): boolean {
    for (let i = 0; i < this.hitCount; i++) if (this.hits[i] === uid) return true;
    return false;
  }

  markHit(uid: number): void {
    // 8칸이 차면 늘린다. 예전에는 9번째부터 기록을 안 해서, 관통이 많은 탄(파도·얼음·벼락)이
    // 같은 적을 매 프레임 다시 때리며 관통 수를 헛써 버렸다.
    this.hits[this.hitCount++] = uid;
  }
}

export class Gem {
  x = 0;
  y = 0;
  vx = 0;
  vy = 0;
  value = 0;
  magnet = false;
  /** 'heal' 이면 경험치가 아니라 체력을, 'chest' 면 카드 한 장을 준다(엘리트가 떨군 보물 상자) */
  kind: 'xp' | 'heal' | 'chest' = 'xp';
  /** heal 일 때 회복량(최대 체력 비율) */
  heal = 0;
  /** 맵에 놓인 것은 둥실거린다 */
  onMap = false;
  bob = 0;
  /** 떨어진 뒤 지난 시간(초) — 아주 멀리서 오래 묵은 경험치 보석은 치운다(경험치는 다음 보석으로 옮긴다) */
  age = 0;
  /** 끌려오기 시작한 뒤 지난 시간(초) — 오래 돌수록 옆으로 도는 힘을 더 빼서 결국 들어오게 한다 */
  magT = 0;
  readonly sprite = new Sprite();

  constructor() {
    this.sprite.anchor.set(0.5);
    this.sprite.visible = false;
  }
}

/** 적이 쏘는 탄. 플레이어만 맞힌다. */
export class EnemyShot {
  x = 0;
  y = 0;
  vx = 0;
  vy = 0;
  damage = 0;
  life = 0;
  radius = 6;
  /** 기사의 검에 맞고 되돌아가는 탄 — 이제 적을 맞힌다 */
  reflected = false;
  // ── 1.22 ──
  /** 번갈아 보여 줄 그림 (없으면 한 장) */
  frames: Texture[] | null = null;
  animT = 0;
  /** 그림이 도는 빠르기 (0이면 날아가는 쪽을 본다) */
  spin = 0;
  /** 초당 이만큼(rad) 플레이어 쪽으로 휜다 */
  homing = 0;
  /** 맞으면 [매혹] 이만큼(초) — 쏜 적 쪽으로 */
  charm = 0;
  src: Enemy | null = null;
  srcUid = 0;
  /** 맞은 자리에 터지는 그림 (fx 이름) */
  hitFx = '';
  /** 기울이지 않고 좌우만 뒤집는다 (하트) */
  upright = false;
  /** 쏜 적 둘레를 도는 남은 시간 · 반지름 · 각 · 각속도 · 날아갈 빠르기 */
  orbit = 0;
  orbitR = 0;
  orbitA = 0;
  orbitW = 0;
  speed = 0;
  cx = 0;
  cy = 0;
  readonly sprite = new Sprite();

  constructor() {
    this.sprite.anchor.set(0.5);
    this.sprite.visible = false;
  }
}

/** 바닥 장판(서리 장막 등). */
export class FieldZone {
  x = 0;
  y = 0;
  radius = 0;
  damage = 0;
  life = 0;
  maxLife = 0;
  slow = 0;
  tick = 0;
  status: StatusApply | null = null;
  /** 이 장판 안에 있으면 받는 피해가 이만큼 줄어든다 (0~1) */
  guard = 0;
  /** 번갈아 보여 줄 그림. 한 장이면 null */
  frames: Texture[] | null = null;
  animT = 0;
  /** 그림이 도는 속도 (rad/s) */
  spin = 0;
  /** 가장 진할 때의 불투명도 */
  alphaMax = 0.9;
  /** 사는 동안 플레이어를 따라다니는가 (풍화설영의 눈보라) */
  follow = false;
  /** 메자이: 이 간격(초)마다 영역 안 가장 센 적에게 황금 광선. 0이면 없음 */
  beam = 0;
  beamT = 0;
  beamDamage = 0;
  /** 메자이: 영역 안에 있으면 초당 최대 체력의 이 비율만큼 회복 */
  heal = 0;
  readonly sprite = new Sprite();

  constructor() {
    this.sprite.anchor.set(0.5);
    this.sprite.visible = false;
    this.sprite.alpha = 0.9;
  }
}
